'use client';

import {
  IconCircleXFilled,
  IconClock,
  IconLoader,
} from '@tabler/icons-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ProcessingStatus } from '@/types/deck';
import type { TemplateStatus } from '@/types/template';

// Serves both the space-files pipeline (pending|processing|ready|failed)
// and the template pipeline, which adds queued|rebuilding.
type BadgeStatus = ProcessingStatus | TemplateStatus;

const statusConfig: Record<
  Exclude<BadgeStatus, 'ready'>,
  {
    label: string;
    icon: typeof IconClock;
    iconClassName?: string;
  }
> = {
  pending: {
    label: 'Pending',
    icon: IconClock,
    iconClassName: 'text-warning',
  },
  processing: {
    label: 'Processing',
    icon: IconLoader,
    iconClassName: 'animate-spin',
  },
  queued: {
    label: 'Queued',
    icon: IconClock,
    iconClassName: 'text-warning',
  },
  rebuilding: {
    label: 'Rebuilding',
    icon: IconLoader,
    iconClassName: 'animate-spin',
  },
  failed: {
    label: 'Failed',
    icon: IconCircleXFilled,
    iconClassName: 'fill-error',
  },
};

/**
 * Renders nothing when the status is `ready`. A healthy library should be
 * quiet — a badge on every card that says "fine" is noise, and it stops the
 * genuinely interesting states from standing out.
 */
export function ProcessingStatusBadge({
  status,
  detail,
  tooltip,
  className,
}: {
  status: BadgeStatus;
  /** Extra text after the label, e.g. rebuild progress ("3/12 slides"). */
  detail?: string;
  /** Hover explanation of the state; copy is the caller's, since what a
   * status means differs per pipeline (space files vs templates). */
  tooltip?: string;
  className?: string;
}) {
  if (status === 'ready') return null;

  const { label, icon: Icon, iconClassName } = statusConfig[status];

  const badge = (
    <Badge
      variant="secondary"
      className={cn('gap-1 px-1.5 text-fg-secondary', className)}
    >
      <Icon className={iconClassName} />
      {detail ? `${label} ${detail}` : label}
    </Badge>
  );

  if (!tooltip) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-64">{tooltip}</TooltipContent>
    </Tooltip>
  );
}
